/**
 * E002-T018: DatabaseDiscoveryService
 * E012-T018: RBAC Database Visibility Filter
 * E018-S012-T002: Extends BlockDiscoveryService
 * S020-8.20.4: Write Permission Filtering for Output Tab
 *
 * Findet alle Databases im aktuellen Workspace.
 * Extends BlockDiscoveryService for shared workspace-wide scanning logic.
 *
 * Reaktivität:
 * - Hört auf docs$ für neue/gelöschte Docs (AFFiNE)
 * - Hört auf blockSuiteDoc.slots.blockUpdated für Block-Änderungen (BlockSuite)
 *
 * RBAC (E012-T018):
 * - accessibleDatabases$ filtert nach Doc_Read Permission via GuardService
 * - writableDatabases$ filtert nach Doc_Update Permission (S020-8.20.4)
 * - UI-Komponenten verwenden gefilterte Liste für Security
 */

import { databaseDeleteDependenciesQuery } from '@affine/graphql';
import type { Block, Store } from '@blocksuite/affine/store';
import { resolveDatabaseStorageModel } from '@blocksuite/affine-block-database';
import {
  getSystemWorkspaceStore,
  peekSystemWorkspaceStore,
} from '@blocksuite/affine-block-streamlain';
import { parseXYWH } from '@blocksuite/global/gfx';
import {
  isHiddenSystemManagedDatabase,
  isValidDatabaseBlockId,
  normalizeDatabaseShardManifest,
} from '@streamlain/database-identity';
import { SL_VIEW_ROLE_KEY } from '@streamlain/view-config';
import { LiveData } from '@toeverything/infra';
import { switchMap } from 'rxjs';

import type { WorkspaceServerService } from '../../cloud';
import type { GraphQLService } from '../../cloud/services/graphql';
import type { DocRecord, DocsService } from '../../doc';
import type { GuardService } from '../../permissions';
import type { WorkspaceService } from '../../workspace';
import { parseXywhToPosition } from '../edge-infrastructure/core/constants';
import {
  BlockDiscoveryService,
  type BlockInfo,
  type TrashcanItem,
} from './block-discovery';
import {
  type DatabaseDeleteDependencies,
  mapDatabaseDeleteDependencies,
} from './database-relation-dependencies';

/**
 * Database-specific doc group with `databases` field for backward compatibility.
 */
export interface DatabaseDocGroup {
  docId: string;
  docTitle: string;
  databases: DatabaseInfo[];
}

/**
 * Column-Info einer Database
 */
export interface ColumnInfo {
  id: string;
  name: string;
  type: string;
  /** For relation columns: target database ID */
  targetDatabaseId?: string;
  /** For relation columns: target doc ID (for CrossDoc relations) */
  targetDocId?: string;
  /** For relation columns: cardinality ('one' or 'many') */
  cardinality?: 'one' | 'many';
  /** For relation columns: inverse cardinality ('one' or 'many') — sl-tah4.4 */
  inverseCardinality?: 'one' | 'many';
  /** sl-vb5r: Raw column data (select options, etc.) */
  data?: Record<string, unknown>;
}

/**
 * Info über eine gefundene Database
 */
export interface DatabaseInfo extends BlockInfo {
  /** Titel der Database */
  databaseTitle: string;
  /** Canvas visibility flag for system-managed databases such as File Index. */
  visible?: boolean;
  /** Storage root document for sharded database schema/manifest data. */
  databaseRootDocId?: string;
  /** Row-shard documents carrying database rows. */
  databaseRowShardDocIds?: string[];
  /** Spalten der Database */
  columns: ColumnInfo[];
  /** true = column/relation schema is managed by a feature and must not be user-deleted casually */
  schemaLocked?: boolean;
  /** true = user row/schema writes are generally managed by a feature */
  systemManaged?: boolean;
  /** Feature owner marker, e.g. "view-config" or "custom-app-standard:content" */
  managedBy?: string;
  /** Normalized StreamLain role marker stored on the database block. */
  streamlainRole?: string;
}

export function isUserFacingWorkspaceDatabase(database: DatabaseInfo): boolean {
  return (
    isValidDatabaseBlockId(database.blockId) &&
    !isHiddenSystemManagedDatabase({
      id: database.blockId,
      managedBy: database.managedBy,
      visible: database.visible,
    })
  );
}

export function isHiddenSystemManagedWorkspaceDatabase(
  database: DatabaseInfo
): boolean {
  return isHiddenSystemManagedDatabase({
    id: database.blockId,
    managedBy: database.managedBy,
    visible: database.visible,
  });
}

export function isHiddenFileIndexWorkspaceDatabase(
  database: DatabaseInfo
): boolean {
  return isHiddenSystemManagedWorkspaceDatabase(database);
}

export function isAccessibleWorkspaceDatabase(database: DatabaseInfo): boolean {
  return (
    !database.isInTrash && !isHiddenSystemManagedWorkspaceDatabase(database)
  );
}

export function isUserFacingActiveWorkspaceDatabase(
  database: DatabaseInfo
): boolean {
  return (
    isUserFacingWorkspaceDatabase(database) &&
    database.isInTrash !== true &&
    typeof database.databaseRootDocId === 'string' &&
    database.databaseRootDocId.trim().length > 0
  );
}

function getNonEmptyText(value: unknown): string | undefined {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : undefined;
  }

  if (
    value &&
    typeof value === 'object' &&
    'toString' in value &&
    typeof value.toString === 'function'
  ) {
    const text = value.toString().trim();
    return text.length > 0 && text !== '[object Object]' ? text : undefined;
  }

  return undefined;
}

type StorageColumnProps = {
  id: string;
  name: string;
  type: string;
  data?: {
    targetDatabaseId?: string;
    targetDocId?: string;
    cardinality?: 'one' | 'many';
    inverseCardinality?: 'one' | 'many';
  };
};

type StorageModelProps = {
  title?: { toString(): string };
  columns?: StorageColumnProps[];
  schemaLocked?: boolean;
  systemManaged?: boolean;
  managedBy?: string;
  visible?: boolean;
  databaseShardManifest?: unknown;
  [SL_VIEW_ROLE_KEY]?: string;
};

type TrashcanSnapshotProps = {
  schemaLocked?: unknown;
  systemManaged?: unknown;
  managedBy?: unknown;
  title?: unknown;
  databaseTitle?: unknown;
  name?: unknown;
  xywh?: string;
  databaseId?: unknown;
  databaseRootDocId?: unknown;
};

function readTrashcanSnapshotProps(
  item: TrashcanItem
): TrashcanSnapshotProps | undefined {
  return (
    item as TrashcanItem & {
      snapshot?: { content?: Array<{ props?: TrashcanSnapshotProps }> };
    }
  ).snapshot?.content?.[0]?.props;
}

/**
 * Sharded storage identity of a trashed database. Items written since the
 * identity fields exist carry them directly; older items fall back to the
 * host block props captured in the snapshot.
 */
export function readTrashcanDatabaseIdentity(item: TrashcanItem): {
  databaseId: string;
  databaseRootDocId?: string;
} {
  const snapshotProps = readTrashcanSnapshotProps(item);
  const databaseId =
    getNonEmptyText(item.databaseId) ??
    getNonEmptyText(snapshotProps?.databaseId) ??
    item.id;
  const databaseRootDocId =
    getNonEmptyText(item.databaseRootDocId) ??
    getNonEmptyText(snapshotProps?.databaseRootDocId);
  return { databaseId, databaseRootDocId };
}

/**
 * Title shown for a trashed database. The live storage title from the root
 * system document survives host-block deletion and reflects renames.
 * Call only after the canonical root has finished loading.
 */
export function resolveTrashcanDatabaseTitle(liveTitle?: string): string {
  return getNonEmptyText(liveTitle) ?? 'Untitled';
}

/**
 * Map storage columns to discovery columns, prepending the built-in title
 * column when the storage model does not list it explicitly.
 */
function mapStorageColumns(
  rawColumns: StorageColumnProps[] | undefined
): ColumnInfo[] {
  const mappedColumns: ColumnInfo[] = (rawColumns ?? []).map(col => ({
    id: col.id,
    name: col.name,
    type: col.type,
    targetDatabaseId: col.data?.targetDatabaseId,
    targetDocId: col.data?.targetDocId,
    cardinality: col.data?.cardinality,
    inverseCardinality: col.data?.inverseCardinality,
    data: col.data,
  }));
  const hasTitleColumn = mappedColumns.some(col => col.type === 'title');
  return hasTitleColumn
    ? mappedColumns
    : [{ id: 'title', name: 'Title', type: 'title' }, ...mappedColumns];
}

function readTrashedStorageModelProps(
  hostStore: Store,
  identity: { databaseId: string; databaseRootDocId?: string }
): StorageModelProps | undefined {
  const rootDocId = identity.databaseRootDocId;
  if (!rootDocId || rootDocId === hostStore.id) return undefined;
  const rootStore = peekSystemWorkspaceStore(hostStore, rootDocId);
  const storageModel = rootStore?.getBlock(identity.databaseId)?.model;
  if (!storageModel || storageModel.flavour !== 'affine:database') {
    return undefined;
  }
  return storageModel.props as StorageModelProps;
}

export class DatabaseDiscoveryService extends BlockDiscoveryService<DatabaseInfo> {
  // Retain only the explicitly referenced trash roots while their host is in
  // the working set. This avoids repeatedly opening/releasing a system doc on
  // every discovery refresh and also observes remote root-title changes.
  private readonly trashRoots = new Map<
    string,
    {
      hostId: string;
      ready: Promise<unknown>;
      release: () => void;
    }
  >();
  constructor(
    docsService: DocsService,
    private readonly guardService: GuardService,
    private readonly workspaceService: WorkspaceService,
    private readonly workspaceServerService: WorkspaceServerService
  ) {
    super(docsService);
  }

  private requireGraphQL(): GraphQLService['gql'] {
    const gql = this.workspaceServerService.server?.gql ?? null;
    if (!gql) {
      throw new Error(
        'GraphQL service not available - workspace not connected to server'
      );
    }
    return gql;
  }

  // ============================================================
  // Abstract Properties Implementation
  // ============================================================

  readonly blockFlavour = 'affine:database';
  readonly trashcanBlockType = 'database';

  // ============================================================
  // Abstract Methods Implementation
  // ============================================================

  extractBlockInfo(block: Block, docRecord: DocRecord): DatabaseInfo | null {
    const blockId = block.model?.id ?? block.id;
    if (!isValidDatabaseBlockId(blockId)) {
      console.warn(
        '[DatabaseDiscovery] Skipping invalid database block id:',
        blockId
      );
      return null;
    }

    const hostProps = block.model?.props as
      | {
          title?: { toString(): string };
          xywh?: string;
        }
      | undefined;
    // Discovery only needs the database's schema metadata (title, columns,
    // manifest, flags). Resolve the storage model directly instead of building
    // a DatabaseBlockDataSource. This keeps the sidebar/discovery path on the
    // pure root-doc metadata contract and prevents future DataSource row-cache
    // lifecycle changes from accidentally turning discovery into a row-shard
    // observer owner again.
    const storageModel = block.model
      ? resolveDatabaseStorageModel(block.model as any)
      : null;
    const props = (storageModel as any)?.props as StorageModelProps | undefined;
    const blockProps = (block.model?.props ?? {}) as Record<string, unknown>;
    const databaseRootDocId =
      typeof blockProps.databaseRootDocId === 'string'
        ? blockProps.databaseRootDocId
        : undefined;
    const databaseShardManifest = this.normalizeShardManifest(
      blockProps.databaseShardManifest ?? props?.databaseShardManifest
    );
    const databaseRowShardDocIds = this.readRowShardDocIds(
      databaseShardManifest
    );

    // Handle empty string as well as undefined/null
    const rawTitle = props?.title?.toString();
    const title = rawTitle && rawTitle.trim() ? rawTitle : 'Untitled';

    const columns = mapStorageColumns(props?.columns);

    return {
      blockId,
      docId: docRecord.id,
      docTitle: docRecord.title$.value || 'Untitled',
      databaseTitle: title,
      visible:
        typeof blockProps.visible === 'boolean'
          ? blockProps.visible
          : typeof props?.visible === 'boolean'
            ? props.visible
            : undefined,
      databaseRootDocId,
      databaseRowShardDocIds,
      columns,
      xywh: hostProps?.xywh,
      isInTrash: false,
      schemaLocked: props?.schemaLocked === true,
      systemManaged: props?.systemManaged === true,
      managedBy:
        typeof props?.managedBy === 'string'
          ? props.managedBy
          : typeof blockProps.managedBy === 'string'
            ? blockProps.managedBy
            : undefined,
      streamlainRole:
        typeof props?.[SL_VIEW_ROLE_KEY] === 'string'
          ? props[SL_VIEW_ROLE_KEY]
          : typeof blockProps[SL_VIEW_ROLE_KEY] === 'string'
            ? (blockProps[SL_VIEW_ROLE_KEY] as string)
            : undefined,
    };
  }

  override async extractTrashcanInfo(
    item: TrashcanItem,
    docRecord: DocRecord,
    hostStore: Store
  ): Promise<DatabaseInfo | null> {
    const identity = readTrashcanDatabaseIdentity(item);
    if (!identity.databaseRootDocId)
      throw new Error('Trashed database has no root identity.');
    const key = `${hostStore.id}\u0000${identity.databaseRootDocId}`;
    let retained = this.trashRoots.get(key);
    if (!retained) {
      const rootStore = getSystemWorkspaceStore(
        hostStore,
        identity.databaseRootDocId
      );
      const { doc, release } = this.docsService.open(
        identity.databaseRootDocId
      );
      const subscription = rootStore.slots.blockUpdated.subscribe(() =>
        this.refresh()
      );
      retained = {
        hostId: hostStore.id,
        ready: doc.waitForSyncReady(),
        release: () => {
          subscription.unsubscribe();
          release();
        },
      };
      this.trashRoots.set(key, retained);
    }
    try {
      await retained.ready;
    } catch (error) {
      retained.release();
      this.trashRoots.delete(key);
      throw error;
    }
    const storageProps = readTrashedStorageModelProps(hostStore, identity);
    if (!storageProps) throw new Error('Trashed database root is unavailable.');
    const snapshotProps = readTrashcanSnapshotProps(item);
    const databaseShardManifest = this.normalizeShardManifest(
      storageProps?.databaseShardManifest
    );

    return {
      blockId: item.id,
      docId: docRecord.id,
      docTitle: docRecord.title$.value || 'Untitled',
      databaseTitle: resolveTrashcanDatabaseTitle(
        storageProps?.title?.toString()
      ),
      databaseRootDocId: identity.databaseRootDocId,
      databaseRowShardDocIds: this.readRowShardDocIds(databaseShardManifest),
      columns: storageProps ? mapStorageColumns(storageProps.columns) : [],
      xywh: readTrashcanSnapshotProps(item)?.xywh,
      isInTrash: true,
      schemaLocked:
        (storageProps?.schemaLocked ?? snapshotProps?.schemaLocked) === true,
      systemManaged:
        (storageProps?.systemManaged ?? snapshotProps?.systemManaged) === true,
      managedBy:
        typeof storageProps?.managedBy === 'string'
          ? storageProps.managedBy
          : typeof snapshotProps?.managedBy === 'string'
            ? snapshotProps.managedBy
            : undefined,
    };
  }

  protected override reconcileTrashcanItems(
    items: TrashcanItem[],
    hostStore: Store
  ): void {
    const roots = new Set(
      items
        .filter(item => item.blockType === 'database')
        .map(item => readTrashcanDatabaseIdentity(item).databaseRootDocId)
    );
    for (const [key, retained] of this.trashRoots) {
      if (
        retained.hostId !== hostStore.id ||
        roots.has(key.slice(hostStore.id.length + 1))
      )
        continue;
      retained.release();
      this.trashRoots.delete(key);
    }
  }

  protected override unsubscribeFromDoc(docId: string): void {
    super.unsubscribeFromDoc(docId);
    for (const [key, retained] of this.trashRoots) {
      if (retained.hostId !== docId) continue;
      retained.release();
      this.trashRoots.delete(key);
    }
  }

  override dispose(): void {
    for (const retained of this.trashRoots.values()) retained.release();
    this.trashRoots.clear();
    super.dispose();
  }

  /**
   * Relation counterparts that break when the database is trashed or
   * permanently deleted, split by whether the current user can clean them up.
   */
  async resolveDeleteDependencies(
    databaseBlockId: string,
    docId: string
  ): Promise<DatabaseDeleteDependencies> {
    const workspace = this.workspaceService.workspace;
    await workspace.engine.doc.flushAndWaitForSynced(docId);
    const result = await this.requireGraphQL()({
      query: databaseDeleteDependenciesQuery,
      variables: {
        input: { workspaceId: workspace.id, docId, blockId: databaseBlockId },
      },
    });
    return mapDatabaseDeleteDependencies(
      databaseBlockId,
      this.blocks$.value.find(database => database.blockId === databaseBlockId),
      result.databaseDeleteDependencies
    );
  }

  // ============================================================
  // Backward-Compatible Aliases
  // ============================================================

  /**
   * Alias for blocks$ - keeps existing API working.
   * @deprecated Use blocks$ from base class
   */
  get databases$(): LiveData<DatabaseInfo[]> {
    return this.blocks$;
  }

  private normalizeShardManifest(value: unknown) {
    try {
      return normalizeDatabaseShardManifest(value);
    } catch {
      return undefined;
    }
  }

  private readRowShardDocIds(
    manifest: ReturnType<typeof normalizeDatabaseShardManifest> | undefined
  ): string[] {
    return (
      manifest?.shards
        .map(shard => shard.serverDocId)
        .filter(
          (docId): docId is string =>
            typeof docId === 'string' && docId.length > 0
        ) ?? []
    );
  }

  /**
   * Alias for blocksByDoc$ - transforms to DatabaseDocGroup format.
   */
  get databasesByDoc$(): LiveData<DatabaseDocGroup[]> {
    return this.blocksByDoc$.map(groups =>
      groups.map(
        (g): DatabaseDocGroup => ({
          docId: g.docId,
          docTitle: g.docTitle,
          databases: g.blocks,
        })
      )
    );
  }

  /**
   * Alias for canvasBlocks$ - keeps existing API working.
   * E018-S010: Canvas-only databases (filtered by xywh presence).
   */
  get canvasDatabases$(): LiveData<DatabaseInfo[]> {
    return this.canvasBlocks$;
  }

  /**
   * Alias for canvasBlocksByDoc$ - transforms to DatabaseDocGroup format.
   * E018-S010: Canvas databases grouped by doc.
   */
  get canvasDatabasesByDoc$(): LiveData<DatabaseDocGroup[]> {
    return this.canvasBlocksByDoc$.map(groups =>
      groups.map(
        (g): DatabaseDocGroup => ({
          docId: g.docId,
          docTitle: g.docTitle,
          databases: g.blocks,
        })
      )
    );
  }

  get userFacingCanvasDatabasesByDoc$(): LiveData<DatabaseDocGroup[]> {
    return this.canvasDatabasesByDoc$.map(groups =>
      groups
        .map(
          (g): DatabaseDocGroup => ({
            ...g,
            databases: g.databases.filter(isUserFacingActiveWorkspaceDatabase),
          })
        )
        .filter(g => g.databases.length > 0)
    );
  }

  // ============================================================
  // E018-S016: Workspace Trashcan
  // ============================================================

  /**
   * E018-S016: All trashed databases across workspace.
   * Filters blocks$ to only include items with isInTrash: true.
   */
  get trashcanDatabases$(): LiveData<DatabaseInfo[]> {
    return this.blocks$.map((databases: DatabaseInfo[]) =>
      databases.filter(db => db.isInTrash === true)
    );
  }

  /**
   * E018-S016: Trashed databases grouped by doc.
   */
  get trashcanDatabasesByDoc$(): LiveData<DatabaseDocGroup[]> {
    return this.trashcanDatabases$.map((databases: DatabaseInfo[]) => {
      const byDoc = new Map<
        string,
        { docTitle: string; databases: DatabaseInfo[] }
      >();

      for (const db of databases) {
        if (!byDoc.has(db.docId)) {
          byDoc.set(db.docId, {
            docTitle: db.docTitle,
            databases: [],
          });
        }
        byDoc.get(db.docId)!.databases.push(db);
      }

      return Array.from(byDoc.entries()).map(([docId, data]) => ({
        docId,
        docTitle: data.docTitle,
        databases: data.databases,
      }));
    });
  }

  // ============================================================
  // RBAC (Database-specific)
  // ============================================================

  /**
   * S020-8.20.4: Permission type for database filtering.
   * - 'read': Doc_Read permission (for Input Tab)
   * - 'write': Doc_Update permission (for Output Tab)
   */
  private readonly readPermissionCache = new Map<
    string,
    { allowed: boolean; timestamp: number }
  >();

  private readonly writePermissionCache = new Map<
    string,
    { allowed: boolean; timestamp: number }
  >();

  /** Cache TTL: 60 seconds */
  private readonly PERMISSION_CACHE_TTL = 60_000;

  /**
   * E012-T018: Filtered databases - only shows DBs in docs the user can read.
   */
  readonly accessibleDatabases$ = LiveData.from<DatabaseInfo[]>(
    this.blocks$.pipe(
      switchMap(async (databases: DatabaseInfo[]) => {
        if (databases.length === 0) {
          return [];
        }

        const docIds = [...new Set(databases.map(db => db.docId))];
        const permissions = await this.checkDocsPermission('read', docIds);

        return databases.filter(
          db =>
            permissions.get(db.docId) === true &&
            isAccessibleWorkspaceDatabase(db)
        );
      })
    ),
    []
  );

  /**
   * E012-T018: Accessible databases grouped by doc.
   */
  readonly accessibleDatabasesByDoc$ = this.accessibleDatabases$.map(
    (databases: DatabaseInfo[]) => {
      return this.groupDatabasesByDoc(databases);
    }
  );

  /**
   * S020-8.20.4: Writable databases - only shows DBs in docs the user can write.
   * Used by Output Tab to filter target table selection.
   */
  readonly writableDatabases$ = LiveData.from<DatabaseInfo[]>(
    this.blocks$.pipe(
      switchMap(async (databases: DatabaseInfo[]) => {
        if (databases.length === 0) {
          return [];
        }

        const docIds = [...new Set(databases.map(db => db.docId))];
        const permissions = await this.checkDocsPermission('write', docIds);

        return databases.filter(
          db =>
            permissions.get(db.docId) === true &&
            isAccessibleWorkspaceDatabase(db)
        );
      })
    ),
    []
  );

  /**
   * S020-8.20.4: Writable databases grouped by doc.
   */
  readonly writableDatabasesByDoc$ = this.writableDatabases$.map(
    (databases: DatabaseInfo[]) => {
      return this.groupDatabasesByDoc(databases);
    }
  );

  /**
   * E018-S016: RBAC-filtered trashed databases.
   * Only shows trashed DBs in docs the user can read.
   */
  readonly accessibleTrashcanDatabases$ = LiveData.from<DatabaseInfo[]>(
    this.trashcanDatabases$.pipe(
      switchMap(async (databases: DatabaseInfo[]) => {
        if (databases.length === 0) {
          return [];
        }

        const docIds = [...new Set(databases.map(db => db.docId))];
        const permissions = await this.checkDocsPermission('read', docIds);

        return databases.filter(db => permissions.get(db.docId) === true);
      })
    ),
    []
  );

  /**
   * E018-S016: RBAC-filtered trashed databases grouped by doc.
   */
  readonly accessibleTrashcanDatabasesByDoc$ =
    this.accessibleTrashcanDatabases$.map((databases: DatabaseInfo[]) => {
      return this.groupDatabasesByDoc(databases);
    });

  readonly userFacingAccessibleTrashcanDatabasesByDoc$ =
    this.accessibleTrashcanDatabases$.map((databases: DatabaseInfo[]) => {
      return this.groupDatabasesByDoc(
        databases.filter(isUserFacingWorkspaceDatabase)
      );
    });

  /**
   * S020-8.20.4: Helper to group databases by doc (DRY).
   */
  private groupDatabasesByDoc(databases: DatabaseInfo[]): DatabaseDocGroup[] {
    const byDoc = new Map<
      string,
      { docTitle: string; databases: DatabaseInfo[] }
    >();

    for (const db of databases) {
      if (!byDoc.has(db.docId)) {
        byDoc.set(db.docId, {
          docTitle: db.docTitle,
          databases: [],
        });
      }
      byDoc.get(db.docId)!.databases.push(db);
    }

    return Array.from(byDoc.entries()).map(([docId, data]) => ({
      docId,
      docTitle: data.docTitle,
      databases: data.databases,
    }));
  }

  /**
   * S020-8.20.4: Generic permission check for multiple docs.
   * @param permission - 'read' for Doc_Read, 'write' for Doc_Update
   * @param docIds - Array of doc IDs to check
   */
  private async checkDocsPermission(
    permission: 'read' | 'write',
    docIds: string[]
  ): Promise<Map<string, boolean>> {
    const result = new Map<string, boolean>();
    const now = Date.now();
    const uncachedDocIds: string[] = [];

    // Select cache based on permission type (SOLID: Single Responsibility)
    const cache =
      permission === 'read'
        ? this.readPermissionCache
        : this.writePermissionCache;

    // Map permission type to GuardService action
    const guardAction = permission === 'read' ? 'Doc_Read' : 'Doc_Update';

    for (const docId of docIds) {
      const cached = cache.get(docId);
      if (cached && now - cached.timestamp < this.PERMISSION_CACHE_TTL) {
        result.set(docId, cached.allowed);
      } else {
        uncachedDocIds.push(docId);
      }
    }

    if (uncachedDocIds.length > 0) {
      try {
        await Promise.all(
          uncachedDocIds.map(async docId => {
            try {
              const allowed = await this.guardService.can(guardAction, docId);
              cache.set(docId, { allowed, timestamp: now });
              result.set(docId, allowed);
            } catch (e) {
              console.warn(
                `[DatabaseDiscovery] Permission check failed for ${docId}:`,
                e
              );
              result.set(docId, false);
            }
          })
        );
      } catch (e) {
        console.warn('[DatabaseDiscovery] Batch permission check failed:', e);
        for (const docId of uncachedDocIds) {
          result.set(docId, false);
        }
      }
    }

    return result;
  }

  /**
   * Invalidate permission cache for a doc.
   * @param docId - Optional doc ID. If omitted, clears all caches.
   */
  invalidatePermissionCache(docId?: string): void {
    if (docId) {
      this.readPermissionCache.delete(docId);
      this.writePermissionCache.delete(docId);
    } else {
      this.readPermissionCache.clear();
      this.writePermissionCache.clear();
    }
    this.refresh();
  }

  // ============================================================
  // Database-specific Methods
  // ============================================================

  /**
   * Find a database by blockId.
   * Alias for findBlock() for backward compatibility.
   */
  findDatabase(blockId: string): DatabaseInfo | undefined {
    return this.findBlock(blockId);
  }

  /**
   * LiveData for a specific database's info.
   * Reactively updates when the database's title or columns change.
   */
  watchDatabase(blockId: string): LiveData<DatabaseInfo | undefined> {
    return this.blocks$.map((databases: DatabaseInfo[]) =>
      databases.find((db: DatabaseInfo) => db.blockId === blockId)
    );
  }

  /**
   * Finds all databases that have a relation pointing TO the given database.
   * Used for bidirectional ghost creation.
   */
  findIncomingRelations(targetDatabaseId: string): DatabaseInfo[] {
    return this.blocks$.value.filter((db: DatabaseInfo) =>
      db.columns.some(
        col =>
          col.type === 'relation' && col.targetDatabaseId === targetDatabaseId
      )
    );
  }

  // ============================================================
  // Automation-related Methods (will move to AutomationDiscoveryService in T004)
  // ============================================================

  /**
   * Finds all automations that reference a given database.
   * @deprecated Will move to AutomationDiscoveryService
   */
  findAutomationsReferencingDatabase(
    databaseBlockId: string
  ): Array<{ docId: string; blockId: string }> {
    const result: Array<{ docId: string; blockId: string }> = [];

    try {
      const docs = this.docsService.list.docs$.value;

      for (const docRecord of docs) {
        if (docRecord.trash$.value) continue;
        // Skip system docs (property stores, workspace meta) — they have no BlockSuite blocks
        if (docRecord.id.includes('$')) continue;

        try {
          const { doc, release } = this.docsService.open(docRecord.id);

          try {
            const blockSuiteDoc = doc.blockSuiteDoc;
            if (!blockSuiteDoc) continue;

            const automationBlocks =
              blockSuiteDoc.getBlocksByFlavour('affine:automation');

            for (const block of automationBlocks) {
              const props = block.model?.props as {
                sourceTableId?: string;
                inputTables?: Array<{ tableId?: string }>;
                targetTableId?: string;
                outputTables?: Array<{ tableId?: string }>;
              };

              const inputTableIds = new Set<string>();
              if (props?.sourceTableId) inputTableIds.add(props.sourceTableId);
              for (const input of props?.inputTables ?? []) {
                if (input.tableId) inputTableIds.add(input.tableId);
              }

              const outputTableIds = new Set<string>();
              if (props?.targetTableId) outputTableIds.add(props.targetTableId);
              for (const output of props?.outputTables ?? []) {
                if (output.tableId) outputTableIds.add(output.tableId);
              }

              if (
                inputTableIds.has(databaseBlockId) ||
                outputTableIds.has(databaseBlockId)
              ) {
                result.push({
                  docId: docRecord.id,
                  blockId: block.model?.id ?? block.id,
                });
              }
            }
          } finally {
            release();
          }
        } catch {
          // Doc not yet loaded/synced — will be picked up on next scan cycle
        }
      }
    } catch (e) {
      console.warn('[DatabaseDiscovery] Failed to find automations:', e);
    }

    return result;
  }

  /**
   * Finds all automations from OTHER docs that reference a given database.
   * @deprecated Will move to AutomationDiscoveryService
   */
  findIncomingAutomations(
    targetDatabaseId: string,
    currentDocId: string
  ): Array<{
    automationId: string;
    automationName: string;
    docId: string;
    docTitle: string;
    direction: 'source' | 'target';
    localDatabaseId: string; // E018-S014: The local DB that is referenced
  }> {
    const result: Array<{
      automationId: string;
      automationName: string;
      docId: string;
      docTitle: string;
      direction: 'source' | 'target';
      localDatabaseId: string;
    }> = [];

    try {
      const docs = this.docsService.list.docs$.value;

      for (const docRecord of docs) {
        if (docRecord.trash$.value) continue;
        if (docRecord.id === currentDocId) continue;
        // Skip system docs (property stores, workspace meta) — they have no BlockSuite blocks
        if (docRecord.id.includes('$')) continue;

        try {
          const { doc, release } = this.docsService.open(docRecord.id);

          try {
            const blockSuiteDoc = doc.blockSuiteDoc;
            if (!blockSuiteDoc) continue;

            const automationBlocks =
              blockSuiteDoc.getBlocksByFlavour('affine:automation');

            for (const block of automationBlocks) {
              const props = block.model?.props as {
                sourceTableId?: string;
                inputTables?: Array<{ tableId?: string }>;
                targetTableId?: string;
                outputTables?: Array<{ tableId?: string }>;
                title?: string;
              };

              const automationId = block.model?.id ?? block.id;
              const automationName = props?.title || 'Automation';
              const docTitle = docRecord.title$.value || 'Untitled';
              const inputTableIds = new Set<string>();
              if (props?.sourceTableId) inputTableIds.add(props.sourceTableId);
              for (const input of props?.inputTables ?? []) {
                if (input.tableId) inputTableIds.add(input.tableId);
              }

              const outputTableIds = new Set<string>();
              if (props?.targetTableId) outputTableIds.add(props.targetTableId);
              for (const output of props?.outputTables ?? []) {
                if (output.tableId) outputTableIds.add(output.tableId);
              }

              if (inputTableIds.has(targetDatabaseId)) {
                result.push({
                  automationId,
                  automationName,
                  docId: docRecord.id,
                  docTitle,
                  direction: 'source',
                  localDatabaseId: targetDatabaseId, // E018-S014
                });
              }

              if (outputTableIds.has(targetDatabaseId)) {
                result.push({
                  automationId,
                  automationName,
                  docId: docRecord.id,
                  docTitle,
                  direction: 'target',
                  localDatabaseId: targetDatabaseId, // E018-S014
                });
              }
            }
          } finally {
            release();
          }
        } catch {
          // Doc not yet loaded/synced — will be picked up on next scan cycle
        }
      }
    } catch (e) {
      console.warn(
        '[DatabaseDiscovery] Failed to find incoming automations:',
        e
      );
    }

    return result;
  }

  /**
   * Finds TRASHED automations in the SAME doc that reference a given database.
   * Used to create ghost nodes showing deleted automations that had connections.
   *
   * The trashcan stores items in props.items as an array of TrashcanItem:
   * - id: original block ID
   * - blockType: 'database' | 'automation'
   * - snapshot: serialized block data
   * - displayName: block name
   */
  findTrashedLocalAutomations(
    targetDatabaseId: string,
    currentDocId: string
  ): Array<{
    automationId: string;
    automationName: string;
    docId: string;
    docTitle: string;
    direction: 'source' | 'target';
    localDatabaseId: string;
    originalPosition?: { x: number; y: number; w: number; h: number };
  }> {
    const result: Array<{
      automationId: string;
      automationName: string;
      docId: string;
      docTitle: string;
      direction: 'source' | 'target';
      localDatabaseId: string;
      originalPosition?: { x: number; y: number; w: number; h: number };
    }> = [];

    try {
      const docRecord = this.docsService.list.doc$(currentDocId).value;
      if (!docRecord) return result;

      const { doc, release } = this.docsService.open(currentDocId);

      try {
        const blockSuiteDoc = doc.blockSuiteDoc;
        if (!blockSuiteDoc) return result;

        // Get the trashcan block (there's only one per doc)
        const trashcanBlocks =
          blockSuiteDoc.getBlocksByFlavour('affine:trashcan');

        if (trashcanBlocks.length === 0) return result;

        const trashcan = trashcanBlocks[0];
        const trashcanProps = trashcan.model?.props as {
          items?: Array<{
            id: string;
            blockType: 'database' | 'automation';
            snapshot: {
              content?: Array<{
                props?: {
                  sourceTableId?: string;
                  inputTables?: Array<{ tableId?: string }>;
                  targetTableId?: string;
                  outputTables?: Array<{ tableId?: string }>;
                  name?: string;
                };
              }>;
            };
            displayName?: string;
          }>;
        };

        const items = trashcanProps?.items ?? [];
        const docTitle = docRecord.title$.value || 'Untitled';

        for (const item of items) {
          // Only process automation items
          if (item.blockType !== 'automation') continue;

          // Extract props from snapshot
          // Snapshot structure: { content: [{ props: { sourceTableId, outputTables, title, xywh } }] }
          const snapshotContent = (
            item.snapshot as {
              content?: Array<{
                props?: {
                  sourceTableId?: string;
                  inputTables?: Array<{ tableId?: string }>;
                  outputTables?: Array<{ tableId: string }>;
                  targetTableId?: string;
                  title?: string;
                  xywh?: string;
                };
              }>;
            }
          )?.content?.[0];
          const snapshotProps = snapshotContent?.props;

          if (!snapshotProps) continue;

          const automationId = item.id;
          const automationName =
            item.displayName || snapshotProps.title || 'Automation';

          // Extract original position from xywh (Single Source: parseXywhToPosition)
          const originalPosition = parseXywhToPosition(snapshotProps.xywh);

          const inputTableIds = new Set<string>();
          if (snapshotProps.sourceTableId) {
            inputTableIds.add(snapshotProps.sourceTableId);
          }
          for (const input of snapshotProps.inputTables ?? []) {
            if (input.tableId) inputTableIds.add(input.tableId);
          }

          const outputTableIds = new Set<string>();
          if (snapshotProps.targetTableId) {
            outputTableIds.add(snapshotProps.targetTableId);
          }
          for (const output of snapshotProps.outputTables ?? []) {
            if (output.tableId) outputTableIds.add(output.tableId);
          }

          // Check if this trashed automation referenced our database as SOURCE (input)
          if (inputTableIds.has(targetDatabaseId)) {
            result.push({
              automationId,
              automationName,
              docId: currentDocId,
              docTitle,
              direction: 'source',
              localDatabaseId: targetDatabaseId,
              originalPosition,
            });
          }

          // Check if this trashed automation referenced our database as TARGET (output)
          if (outputTableIds.has(targetDatabaseId)) {
            result.push({
              automationId,
              automationName,
              docId: currentDocId,
              docTitle,
              direction: 'target',
              localDatabaseId: targetDatabaseId,
              originalPosition,
            });
          }
        }
      } finally {
        release();
      }
    } catch (e) {
      console.warn(
        '[DatabaseDiscovery] Failed to find trashed local automations:',
        e
      );
    }

    return result;
  }

  /**
   * Get the original position of a trashed block (database or automation).
   * Used to position ghosts at the location where the deleted block was.
   */
  getTrashedBlockPosition(
    blockId: string,
    currentDocId: string
  ): { x: number; y: number; w: number; h: number } | null {
    try {
      const { doc, release } = this.docsService.open(currentDocId);

      try {
        const blockSuiteDoc = doc.blockSuiteDoc;
        if (!blockSuiteDoc) return null;

        const trashcanBlocks =
          blockSuiteDoc.getBlocksByFlavour('affine:trashcan');
        if (trashcanBlocks.length === 0) return null;

        const trashcan = trashcanBlocks[0];
        const trashcanProps = trashcan.model?.props as {
          items?: Array<{
            id: string;
            snapshot: {
              content?: Array<{
                props?: {
                  xywh?: string;
                };
              }>;
            };
          }>;
        };

        const items = trashcanProps?.items ?? [];
        const item = items.find(i => i.id === blockId);

        if (!item) return null;

        const xywh = item.snapshot?.content?.[0]?.props?.xywh;
        if (!xywh) return null;

        const parsedXYWH = parseXYWH(xywh);
        if (!parsedXYWH) return null;
        const [x, y, w, h] = parsedXYWH;
        return { x, y, w, h };
      } finally {
        release();
      }
    } catch (e) {
      console.warn(
        '[DatabaseDiscovery] Failed to get trashed block position:',
        e
      );
      return null;
    }
  }
}
